import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

const OrgUnitTypesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [showControls, setShowControls] = useState(true);
  const [orgUnitTypes, setOrgUnitTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Unified modal state
  const [orgUnitTypeModal, setOrgUnitTypeModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '', code: '', description: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, orgUnitType: null });

  const fetchOrgUnitTypes = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getOrgUnitTypes();
      setOrgUnitTypes(data);
    } catch (err) {
      showAlert('Failed to load organizational unit types.', 'error', err);
      console.error('Error fetching organizational unit types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgUnitTypes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveOrgUnitType = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (orgUnitTypeModal.isEdit) {
        // Edit existing organizational unit type
        if (!orgUnitTypeModal.originalId) {
          showAlert('Invalid organizational unit type data for update', 'error');
          return;
        }
        
        await organizationApi.updateOrgUnitType(orgUnitTypeModal.originalId, {
          name: orgUnitTypeModal.formData.name,
          code: orgUnitTypeModal.formData.code,
          description: orgUnitTypeModal.formData.description || null
        });
        showAlert('Organizational unit type updated successfully', 'success');
      } else {
        // Add new organizational unit type
        await organizationApi.createOrgUnitType({
          name: orgUnitTypeModal.formData.name,
          code: orgUnitTypeModal.formData.code,
          description: orgUnitTypeModal.formData.description || null
        });
        showAlert('Organizational unit type added successfully', 'success');
      }
      
      await fetchOrgUnitTypes();
      // Reset modal state
      setOrgUnitTypeModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '', description: '' },
        originalId: null
      });
    } catch (err) {
      const action = orgUnitTypeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} organizational unit type.`, 'error', err);
      console.error(`Error ${action}ing organizational unit type:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOrgUnitType = (orgUnitType) => {
    setDeleteModal({ open: true, orgUnitType });
  };

  const confirmDelete = async () => {
    const typeToDelete = deleteModal.orgUnitType;
    setDeleteModal({ open: false, orgUnitType: null });
    
    if (!typeToDelete?.id) {
      showAlert('Invalid organizational unit type data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteOrgUnitType(typeToDelete.id);
      await fetchOrgUnitTypes();
      showAlert('Organizational unit type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting organizational unit type:', err);
      showAlert(err.message || 'Failed to delete organizational unit type.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrgUnitTypes = orgUnitTypes.filter(type => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return type.name.toLowerCase().includes(lowerSearch) || 
           (type.code && type.code.toLowerCase().includes(lowerSearch)) ||
           (type.description && type.description.toLowerCase().includes(lowerSearch));
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Organizational Unit Types Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Organizational Unit Type"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            {ability.can(ACTIONS.CREATE, SUBJECTS.ORG_UNIT_TYPE) && (
              <HeaderButton onClick={() => setOrgUnitTypeModal({
                open: true,
                isEdit: false,
                formData: { name: '', code: '', description: '' },
                originalId: null
              })}>
                <FaPlus /> Add Organizational Unit Type
              </HeaderButton>
            )}
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredOrgUnitTypes.length}
                </span>
                {filteredOrgUnitTypes.length === 1 ? 'organizational unit type' : 'organizational unit types'} found
              </div>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredOrgUnitTypes.map((orgUnitType) => (
                  <Grid item key={orgUnitType.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '4px'
                            }}
                          >
                            {orgUnitType.name}
                          </Typography>
                          {orgUnitType.code && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                display: 'block',
                                marginBottom: '8px'
                              }}
                            >
                              Code: {orgUnitType.code}
                            </Typography>
                          )}
                          {orgUnitType.description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                marginBottom: '8px'
                              }}
                            >
                              {orgUnitType.description}
                            </Typography>
                          )}
                          {orgUnitType._count?.businessUnits > 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontStyle: 'italic'
                              }}
                            >
                              Used by {orgUnitType._count.businessUnits} business unit{orgUnitType._count.businessUnits !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          {ability.can(ACTIONS.UPDATE, SUBJECTS.ORG_UNIT_TYPE) && (
                            <Button
                              onClick={() => setOrgUnitTypeModal({
                                open: true,
                                isEdit: true,
                                formData: { 
                                  name: orgUnitType.name,
                                  code: orgUnitType.code || '',
                                  description: orgUnitType.description || ''
                                },
                                originalId: orgUnitType.id
                              })}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#0d6efd',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                  color: '#0d6efd'
                                }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {ability.can(ACTIONS.DELETE, SUBJECTS.ORG_UNIT_TYPE) && (
                            <Button
                              onClick={() => handleDeleteOrgUnitType(orgUnitType)}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  color: '#dc3545'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing organizational unit types */}
      <Modal
        isOpen={orgUnitTypeModal.open}
        onClose={() => setOrgUnitTypeModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '', description: '' },
          originalId: null
        })}
        onSubmit={handleSaveOrgUnitType}
        title={orgUnitTypeModal.isEdit ? "Edit Organizational Unit Type" : "Add Organizational Unit Type"}
        submitText={orgUnitTypeModal.isEdit ? "Update" : "Add"}
      >
        <Box mb={2}>
          <TextField
            id="org-unit-type-name"
            name="name"
            label="Organizational Unit Type Name"
            required
            value={orgUnitTypeModal.formData.name}
            onChange={(e) => setOrgUnitTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter organizational unit type name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="org-unit-type-code"
            name="code"
            label="Code"
            required
            value={orgUnitTypeModal.formData.code}
            onChange={(e) => setOrgUnitTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter organizational unit type code"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="org-unit-type-description"
            name="description"
            label="Description"
            value={orgUnitTypeModal.formData.description}
            onChange={(e) => setOrgUnitTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, description: e.target.value }
            }))}
            placeholder="Enter description (optional)"
            autoComplete="off"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, orgUnitType: null })}
        onConfirm={confirmDelete}
        title="Delete Organizational Unit Type"
        message={`Are you sure you want to delete the organizational unit type "${deleteModal.orgUnitType?.name}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default OrgUnitTypesConfiguration;